/* eslint-disable max-len */
import React from 'react';

import { reverse } from 'named-urls';

import { Button } from 'components/Button';

import { HomeSectionHeader, HomeSectionHeaderContainer } from 'containers/Home/styles';
import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import { Solution } from './types';
import { SolutionCell, SolutionDescription, SolutionImage, SolutionTitle, SolutionsContainer } from './styles';

const SolutionsPane: React.FunctionComponent<{ displayHeader?: boolean }> = ({ displayHeader = true }) => {
  const {
    session: { user },
    routing,
  } = useStore();

  if (!user) {
    return null;
  }

  const { licenses } = user;

  const eventsEnabled = licenses.events || false;
  const alarmsEnabled = licenses.protectorAlarms || false;
  const auditsEnabled = licenses.workflows || false;
  const documentsEnabled = licenses.documents || false;

  const solutions: Solution[] = [
    {
      title: 'Events',
      description:
        'Centralised platform for seamless incident reporting, investigation, and follow-up action tracking across your organisation.',
      imgName: 'incidentsSolution.png',
      enabled: eventsEnabled,
      route: eventsEnabled
        ? routes.dashboard.events.toString()
        : reverse(routes.dashboard.disableFeature.details, { featureId: 'events' }),
    },
    {
      title: 'Documents',
      description:
        'Access our AI-powered document management system. Create, edit, store, and manage documents securely in one centralised platform.',
      imgName: 'documentsSolution.png',
      enabled: documentsEnabled,
      route: documentsEnabled
        ? routes.dashboard.documents.toString()
        : reverse(routes.dashboard.disableFeature.details, { featureId: 'documents' }),
    },
    {
      title: 'Audits',
      description:
        'Create audits & inspections templates, complete inspections through the mobile app, and coordinate corrective tasks.',
      imgName: 'auditsSolution.png',
      enabled: auditsEnabled,
      route: auditsEnabled
        ? routes.dashboard.audits.toString()
        : reverse(routes.dashboard.disableFeature.details, { featureId: 'audits' }),
    },
    {
      title: 'Lone Working',
      description:
        'Safeguard your staff and boost H&S compliance through our 24/7 monitored lone working app or lone working devices.',
      imgName: 'loneWorkingSolution.png',
      enabled: alarmsEnabled,
      route: alarmsEnabled
        ? routes.dashboard.alarms.red.toString()
        : reverse(routes.dashboard.disableFeature.details, { featureId: 'protector' }),
    },
  ];

  const goToModule = (route: string): void => {
    routing.push(route);
  };

  return (
    <>
      {displayHeader ? (
        <HomeSectionHeaderContainer>
          <HomeSectionHeader>Our Solutions</HomeSectionHeader>
        </HomeSectionHeaderContainer>
      ) : null}
      <SolutionsContainer>
        {solutions.map(({ title, description, imgName, enabled, route }, index) => (
          <SolutionCell key={`solution_${index}`}>
            <SolutionTitle>{title}</SolutionTitle>
            <SolutionImage src={`/solutions/${imgName}`} alt={`${title} solution image`} />
            <SolutionDescription>{description}</SolutionDescription>

            <Button
              onClick={() => {
                goToModule(route);
              }}
              style={{
                padding: '0px',
                alignSelf: 'flex-start',
              }}
            >
              {enabled ? 'View Module' : 'Unlock Module'}
            </Button>
          </SolutionCell>
        ))}
      </SolutionsContainer>
    </>
  );
};

export default SolutionsPane;
